import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  SignalSlashIcon,
  CloudIcon,
  SignalIcon,
  MapIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  ClockIcon,
  ArrowPathIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { TokenCalculator } from '@/components/embed/TokenCalculator';
import { PricingCarousel } from '@/components/embed/PricingCarousel';
import { api } from '@/services/api';

interface Feature {
  title: string;
  desc: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
}

const features: Feature[] = [
  {
    title: 'Offline Charts',
    desc: 'Download and cache charts for use without connectivity',
    icon: SignalSlashIcon,
  },
  {
    title: 'Weather Overlays',
    desc: 'Real-time weather data and multi-day forecasts',
    icon: CloudIcon,
  },
  {
    title: 'AIS Vessel Tracking',
    desc: 'Live vessel positions and collision avoidance',
    icon: SignalIcon,
  },
  {
    title: 'High-Res Bathymetry',
    desc: 'Detailed depth contours and seabed mapping',
    icon: MapIcon,
  },
  {
    title: 'Multi-Device Sync',
    desc: 'Access charts across all your navigation devices',
    icon: DevicePhoneMobileIcon,
  },
  {
    title: 'Route Planning',
    desc: 'Plan, save, and share routes with waypoints',
    icon: MapPinIcon,
  },
  {
    title: 'Tidal Data',
    desc: 'Current and predicted tidal information',
    icon: ClockIcon,
  },
  {
    title: 'Automatic Updates',
    desc: 'Charts update automatically when connected',
    icon: ArrowPathIcon,
  },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: packages } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
    staleTime: 5 * 60 * 1000,
  });

  const handlePlanSelect = (slug: string) => {
    navigate(`/register?package=${encodeURIComponent(slug)}`);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 font-sans selection:bg-cyan-500/30 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img
                src="/vortex-logo-white.svg"
                alt="Vortex Marine"
                className="w-8 h-8"
              />
              <span className="text-xl font-bold text-white tracking-tight">
                Vortex Marine
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a
                href="#features"
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Pricing
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <a
                href="https://vortexmarine.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors flex items-center gap-1.5 mr-4"
              >
                Shop Hardware <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-medium text-white hover:text-cyan-400 transition-colors px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-600 bg-slate-800/50"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="text-sm font-bold text-slate-900 bg-cyan-500 hover:bg-cyan-400 transition-colors px-5 py-2 rounded-lg"
              >
                Register
              </button>
            </div>

            <button
              type="button"
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800 bg-slate-900/95 backdrop-blur-sm">
            <div className="px-4 py-4 space-y-3">
              <a
                href="#features"
                className="block px-3 py-2 text-slate-300 hover:text-white transition rounded-lg hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="block px-3 py-2 text-slate-300 hover:text-white transition rounded-lg hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              <a
                href="https://vortexmarine.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-slate-300 hover:text-white transition rounded-lg hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop Hardware <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              </a>
              <div className="pt-2 border-t border-slate-800 flex flex-col gap-2">
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                  className="w-full text-sm font-medium text-white px-4 py-2.5 rounded-lg border border-slate-700 hover:bg-slate-800 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}
                  className="w-full text-sm font-bold text-slate-900 bg-cyan-500 hover:bg-cyan-400 transition-colors px-4 py-2.5 rounded-lg"
                >
                  Register
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-32 px-4 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-slate-800/80 border border-slate-700 text-sm font-medium text-slate-300 mb-8 shadow-sm">
              Everything you need for marine navigation
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
              Connected When You Can Be.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Capable When You Can't.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              VortexNav keeps you on course whether you're connected or not.
              Live data at the dock, full professional charts at sea.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#pricing"
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 text-white font-bold text-lg transition-colors text-center"
              >
                View Pricing
              </a>
            </div>
          </div>
        </section>

        {/* Features + Calculator */}
        <section
          id="features"
          className="py-20 bg-slate-900/50 border-t border-slate-800 relative"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12">
              {/* Left Column: Features */}
              <div className="lg:col-span-5 lg:pr-8">
                <div className="sticky top-32">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Device + Platform Features
                  </h2>
                  <p className="text-lg text-slate-400 mb-10">
                    Pair a Vortex Scout with a subscription plan to unlock
                    professional-grade navigation, offline-first.
                  </p>

                  <div className="space-y-6">
                    {features.map((feature, idx) => {
                      const Icon = feature.icon;
                      return (
                        <div key={idx} className="flex items-start gap-4">
                          <div className="mt-0.5 p-2 rounded-lg bg-cyan-500/10 shrink-0">
                            <Icon className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-lg mb-1">
                              {feature.title}
                            </h3>
                            <p className="text-slate-400 leading-relaxed">
                              {feature.desc}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Calculator */}
              <div id="pricing" className="lg:col-span-7">
                <TokenCalculator onSelect={handlePlanSelect} />
              </div>
            </div>
          </div>
        </section>

        {/* All Plans */}
        {packages && packages.length > 0 && (
          <section className="py-20 bg-slate-900 border-t border-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  All Plans
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                  Compare plans side by side to find the right fit for your navigation needs.
                </p>
              </div>
              <PricingCarousel
                packages={packages}
                onSelect={handlePlanSelect}
              />
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img
                src="/vortex-logo-white.svg"
                alt="Vortex Marine"
                className="w-6 h-6 opacity-50"
              />
              <span className="text-sm text-slate-500">
                &copy; 2026 Vortex Marine. All rights reserved.
              </span>
            </div>

            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <a
                href="#features"
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Pricing
              </a>
              <a
                href="https://vortexmarine.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Shop Hardware
              </a>
              <a
                href="mailto:billing@vortexmarine.com"
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Support
              </a>
            </div>

            <div className="text-sm text-slate-500">Built in New Zealand</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
